/**
 * AlerteTab - Alerte documente + mentenanță + alimentări suspecte
 * Cu filtrare pe categorii (Toate, Rovinietă, Asigurare, ITP, CASCO, Copie Conformă, Mentenanță, Șoferi)
 */
import { memo, useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, CheckCircle, Eye, Fuel, AlertTriangle, Wrench, Gauge, Shield, FileCheck, Car, User, Receipt } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${API}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Category definitions
const CATEGORIES = [
  { id: 'toate', label: 'Toate', icon: Bell },
  { id: 'tax_road_expiry', label: 'Rovinietă', icon: Car, fieldKeys: ['tax_road_expiry'] },
  { id: 'insurance_expiry', label: 'Asigurare', icon: Shield, fieldKeys: ['insurance_expiry'] },
  { id: 'inspection_expiry', label: 'ITP', icon: FileCheck, fieldKeys: ['inspection_expiry'] },
  { id: 'itp_echipamente', label: 'ITP Echipamente', icon: Wrench },
  { id: 'casco_expiry', label: 'CASCO', icon: Shield, fieldKeys: ['casco_expiry'] },
  { id: 'copie_conforma_expiry', label: 'Copie Conformă', icon: FileCheck, fieldKeys: ['copie_conforma_expiry'] },
  { id: 'mentenanta', label: 'Mentenanță', icon: Wrench },
  { id: 'soferi', label: 'Șoferi', icon: User, fieldKeys: ['id_expiry', 'driver_licence_expiry', 'work_medicine_expiry'] },
  { id: 'alimentari', label: 'Alimentări', icon: Fuel },
  { id: 'scadente_furnizori', label: 'Scadențe furnizori', icon: Receipt },
];

const fmtLei = (v) => `${Number(v || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`;

const DUE_STYLES = {
  expired: { box: 'bg-red-50 border-red-200 text-red-800', icon: 'text-red-600' },
  high: { box: 'bg-orange-50 border-orange-200 text-orange-800', icon: 'text-orange-600' },
  medium: { box: 'bg-yellow-50 border-yellow-200 text-yellow-800', icon: 'text-yellow-600' },
};

function SupplierDueCard({ alert }) {
  const st = DUE_STYLES[alert.urgency] || DUE_STYLES.medium;
  return (
    <div className={`p-3 border rounded-lg ${st.box}`} data-testid={`supplier-due-alert-${alert.id}`}>
      <div className="flex items-start gap-3">
        <Receipt className={`w-5 h-5 mt-0.5 ${st.icon}`} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{alert.furnizor}</p>
          <p className="text-sm">
            {alert.tip_doc && alert.tip_doc !== 'Factură' ? alert.tip_doc : 'Factura'} <span className="font-medium">{alert.nr_factura}</span> din {new Date(alert.data).toLocaleDateString('ro-RO')}
            {' — '}
            {alert.zile_ramase < 0 ? (
              <span className="font-bold">RESTANTĂ de {Math.abs(alert.zile_ramase)} zile</span>
            ) : alert.zile_ramase === 0 ? (
              <span className="font-bold">scadentă AZI</span>
            ) : (
              <>scadentă în <span className="font-bold">{alert.zile_ramase} zile</span></>
            )}
          </p>
          <p className="text-xs mt-1 opacity-80">
            Scadență: {new Date(alert.scadent).toLocaleDateString('ro-RO')} · Total factură: {fmtLei(alert.total)}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase opacity-70">Rest de plată</p>
          <p className="font-bold text-sm" data-testid={`supplier-due-neachitat-${alert.id}`}>{fmtLei(alert.rest ?? alert.neachitat)}</p>
          {alert.achitat > 0 && <p className="text-[10px] opacity-70">achitat {fmtLei(alert.achitat)}</p>}
        </div>
      </div>
    </div>
  );
}

function AlertCard({ alert, onViewPendingDocument }) {
  const isScheduled = alert.urgency === 'scheduled' || alert.hasPendingDoc;

  const bgColor = isScheduled
    ? 'bg-green-50 border-green-200'
    : alert.urgency === 'expired' ? 'bg-red-50 border-red-200'
    : alert.urgency === 'high' ? 'bg-orange-50 border-orange-200'
    : 'bg-yellow-50 border-yellow-200';

  const textColor = isScheduled
    ? 'text-green-800'
    : alert.urgency === 'expired' ? 'text-red-800'
    : alert.urgency === 'high' ? 'text-orange-800'
    : 'text-yellow-800';

  const iconColor = isScheduled
    ? 'text-green-600'
    : alert.urgency === 'expired' ? 'text-red-600'
    : alert.urgency === 'high' ? 'text-orange-600'
    : 'text-yellow-600';

  const typeLabel = alert.type === 'car'
    ? `${alert.carName} (${alert.licensePlate})`
    : alert.type === 'equipment'
    ? alert.name || alert.label
    : `Angajat: ${alert.driverName}`;

  return (
    <div className={`p-3 border rounded-lg ${bgColor} ${textColor}`} data-testid={`alert-${alert.id}`}>
      <div className="flex items-start gap-3">
        {isScheduled ? (
          <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
        ) : (
          <Bell className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-sm">{typeLabel}</p>
              <p className="text-sm mt-0.5">
                <span className="font-medium">{alert.item}</span>
                {alert.urgency === 'expired' && !isScheduled ? (
                  <span className="ml-2 font-bold">EXPIRAT!</span>
                ) : (
                  <span className="ml-2">
                    expiră în <span className="font-bold">{alert.daysUntilExpiry} zile</span>
                  </span>
                )}
              </p>
              <p className="text-xs mt-0.5 opacity-75">
                Data expirării: {(alert.expiryDate instanceof Date ? alert.expiryDate : new Date(alert.expiryDate)).toLocaleDateString('ro-RO')}
                <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/50">
                  {alert.type === 'car' ? 'AUTO' : alert.type === 'equipment' ? 'ECHIPAMENT' : 'SOFER'}
                </span>
              </p>
              {isScheduled && alert.pendingDoc && (
                <div className="mt-2 p-2 bg-green-100 rounded-lg border border-green-300">
                  <p className="text-xs font-medium text-green-700 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Document nou programat din {new Date(alert.pendingDoc.activation_date).toLocaleDateString('ro-RO')}
                    {alert.pendingDoc.expiry_date && (
                      <span className="ml-1 opacity-75">
                        (valabil până la {new Date(alert.pendingDoc.expiry_date).toLocaleDateString('ro-RO')})
                      </span>
                    )}
                  </p>
                  {alert.pendingDoc.document && onViewPendingDocument && (
                    <button
                      onClick={() => onViewPendingDocument(alert.pendingDoc, alert.carName, alert.licensePlate)}
                      className="mt-1 text-xs text-green-700 hover:text-green-900 underline flex items-center gap-1"
                      data-testid={`view-pending-doc-${alert.id}`}
                    >
                      <Eye className="w-3 h-3" />
                      Vezi documentul
                    </button>
                  )}
                </div>
              )}
            </div>
            {alert.urgency === 'expired' && !isScheduled && (
              <span className="px-2 py-1 text-xs font-bold bg-red-600 text-white rounded flex-shrink-0">URGENT</span>
            )}
            {isScheduled && (
              <span className="px-2 py-1 text-xs font-bold bg-green-600 text-white rounded flex-shrink-0">REZOLVAT</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MaintenanceAlertCard({ alert }) {
  const bgColor = alert.urgency === 'expired' ? 'bg-red-50 border-red-200'
    : alert.urgency === 'high' ? 'bg-orange-50 border-orange-200'
    : 'bg-yellow-50 border-yellow-200';
  const textColor = alert.urgency === 'expired' ? 'text-red-800'
    : alert.urgency === 'high' ? 'text-orange-800'
    : 'text-yellow-800';
  const iconColor = alert.urgency === 'expired' ? 'text-red-600'
    : alert.urgency === 'high' ? 'text-orange-600'
    : 'text-yellow-600';

  return (
    <div className={`p-3 border rounded-lg ${bgColor} ${textColor}`} data-testid={`maint-alert-${alert.id}`}>
      <div className="flex items-start gap-3">
        <Wrench className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{alert.car_name} ({alert.license_plate})</p>
          <p className="text-sm mt-0.5">
            <span className="font-medium">{alert.tip}</span>
            {alert.subtype === 'km' ? (
              alert.urgency === 'expired' ? (
                <span className="ml-2 font-bold">DEPĂȘIT cu {Math.abs(alert.km_remaining).toLocaleString()} km</span>
              ) : (
                <span className="ml-2">mai sunt <span className="font-bold">{alert.km_remaining.toLocaleString()} km</span></span>
              )
            ) : (
              alert.urgency === 'expired' ? (
                <span className="ml-2 font-bold">DEPĂȘIT cu {Math.abs(alert.days_remaining)} zile</span>
              ) : (
                <span className="ml-2">expiră în <span className="font-bold">{alert.days_remaining} zile</span></span>
              )
            )}
          </p>
          <p className="text-xs mt-0.5 opacity-75">
            {alert.subtype === 'km' ? (
              <span className="flex items-center gap-1">
                <Gauge className="w-3 h-3" />
                Actual: {alert.current_km?.toLocaleString()} km / Programat: {alert.next_km?.toLocaleString()} km
              </span>
            ) : (
              <span>Data programată: {alert.next_date}</span>
            )}
            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/50">MENTENANȚĂ</span>
          </p>
        </div>
        {alert.urgency === 'expired' && (
          <span className="px-2 py-1 text-xs font-bold bg-red-600 text-white rounded flex-shrink-0">URGENT</span>
        )}
      </div>
    </div>
  );
}

const AlerteTab = memo(function AlerteTab({ alerts, onViewPendingDocument }) {
  const [fuelAlerts, setFuelAlerts] = useState([]);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [equipmentAlerts, setEquipmentAlerts] = useState([]);
  const [supplierDue, setSupplierDue] = useState({ alerts: [], total_neachitat: 0 });
  const [loadingFuel, setLoadingFuel] = useState(true);
  const [loadingMaint, setLoadingMaint] = useState(true);
  const [loadingEquip, setLoadingEquip] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('toate');

  useEffect(() => { fetchFuelAlerts(); fetchMaintenanceAlerts(); fetchEquipmentAlerts(); fetchSupplierDue(); }, []);

  const fetchSupplierDue = async () => {
    try {
      const response = await axios.get(`${API_URL}/furnizori/alerte-scadente?zile=7`, getAuthHeaders());
      setSupplierDue({ alerts: response.data.alerts || [], total_neachitat: response.data.total_neachitat || 0 });
    } catch (error) {
      console.error('Error fetching supplier due alerts:', error);
    }
  };

  const fetchFuelAlerts = async () => {
    try {
      const response = await axios.get(`${API_URL}/alerts/fuel-suspicious`, getAuthHeaders());
      setFuelAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching fuel alerts:', error);
    } finally {
      setLoadingFuel(false);
    }
  };

  const fetchMaintenanceAlerts = async () => {
    try {
      const response = await axios.get(`${API_URL}/mentenanta/alerte`, getAuthHeaders());
      setMaintenanceAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching maintenance alerts:', error);
    } finally {
      setLoadingMaint(false);
    }
  };

  const fetchEquipmentAlerts = async () => {
    try {
      const response = await axios.get(`${API_URL}/equipments/alerts/itp`, getAuthHeaders());
      setEquipmentAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching equipment alerts:', error);
    } finally {
      setLoadingEquip(false);
    }
  };

  const handleVerifyFuelAlert = async (alertId) => {
    setVerifyingId(alertId);
    try {
      await axios.post(`${API_URL}/alerts/fuel-suspicious/${alertId}/verify`, {}, getAuthHeaders());
      toast.success('Alimentare marcată ca verificată');
      setFuelAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch {
      toast.error('Eroare la verificare');
    } finally {
      setVerifyingId(null);
    }
  };

  // Transform equipment alerts to same format as document alerts
  const equipmentDocAlerts = useMemo(() => {
    return equipmentAlerts.map(ea => ({
      id: `eq_${ea.id}`,
      type: 'equipment',
      fieldKey: 'itp_echipamente',
      name: ea.name,
      item: 'ITP',
      label: `ITP Echipament: ${ea.name}`,
      fieldLabel: ea.type ? `${ea.type}${ea.nr_iscir ? ` — ISCIR: ${ea.nr_iscir}` : ''}` : '',
      expiryDate: new Date(ea.itp_expiry),
      daysUntilExpiry: ea.days_left,
      urgency: ea.days_left < 0 ? 'expired' : ea.days_left <= 10 ? 'high' : 'warning',
      message: ea.message
    }));
  }, [equipmentAlerts]);

  // Sort all document alerts (including equipment)
  const sortedAlerts = useMemo(() => {
    const combined = [...(alerts || []), ...equipmentDocAlerts];
    return combined.sort((a, b) => {
      const aScheduled = a.urgency === 'scheduled' || a.hasPendingDoc;
      const bScheduled = b.urgency === 'scheduled' || b.hasPendingDoc;
      if (aScheduled && !bScheduled) return 1;
      if (!aScheduled && bScheduled) return -1;
      return (a.daysUntilExpiry ?? 999) - (b.daysUntilExpiry ?? 999);
    });
  }, [alerts, equipmentDocAlerts]);

  // Count alerts per category
  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const cat of CATEGORIES) {
      if (cat.id === 'toate') {
        counts.toate = sortedAlerts.length + maintenanceAlerts.length + fuelAlerts.length + supplierDue.alerts.length;
      } else if (cat.id === 'scadente_furnizori') {
        counts.scadente_furnizori = supplierDue.alerts.length;
      } else if (cat.id === 'mentenanta') {
        counts.mentenanta = maintenanceAlerts.length;
      } else if (cat.id === 'alimentari') {
        counts.alimentari = fuelAlerts.length;
      } else if (cat.id === 'itp_echipamente') {
        counts.itp_echipamente = equipmentAlerts.length;
      } else if (cat.id === 'soferi') {
        counts.soferi = sortedAlerts.filter(a => a.type === 'driver').length;
      } else if (cat.fieldKeys) {
        counts[cat.id] = sortedAlerts.filter(a => a.type === 'car' && cat.fieldKeys.includes(a.fieldKey)).length;
      }
    }
    return counts;
  }, [sortedAlerts, maintenanceAlerts, fuelAlerts, equipmentAlerts, supplierDue]);

  // Filter alerts by active category
  const filteredDocAlerts = useMemo(() => {
    if (activeCategory === 'toate') return sortedAlerts;
    if (activeCategory === 'mentenanta' || activeCategory === 'alimentari' || activeCategory === 'scadente_furnizori') return [];
    if (activeCategory === 'itp_echipamente') return sortedAlerts.filter(a => a.type === 'equipment');
    if (activeCategory === 'soferi') return sortedAlerts.filter(a => a.type === 'driver');
    const cat = CATEGORIES.find(c => c.id === activeCategory);
    if (cat?.fieldKeys) return sortedAlerts.filter(a => a.type === 'car' && cat.fieldKeys.includes(a.fieldKey));
    return sortedAlerts;
  }, [sortedAlerts, activeCategory]);

  const showMaintenance = activeCategory === 'toate' || activeCategory === 'mentenanta';
  const showFuel = activeCategory === 'toate' || activeCategory === 'alimentari';
  const showSupplierDue = activeCategory === 'scadente_furnizori' || (activeCategory === 'toate' && supplierDue.alerts.length > 0);
  const showDocs = !['mentenanta', 'alimentari', 'scadente_furnizori'].includes(activeCategory);

  return (
    <div className="space-y-4">
      {/* Category filter tabs */}
      <div className="flex flex-wrap gap-1.5 p-3 bg-slate-50 rounded-lg border" data-testid="alerte-categories">
        {CATEGORIES.map(cat => {
          const count = categoryCounts[cat.id] || 0;
          const isActive = activeCategory === cat.id;
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`alerte-cat-${cat.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white text-slate-600 border hover:bg-slate-100'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {cat.label}
              {count > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-red-100 text-red-700'
                }`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Document alerts section */}
      {showDocs && filteredDocAlerts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bell className="w-5 h-5" />
              {activeCategory === 'toate' ? 'Alerte Documente' : CATEGORIES.find(c => c.id === activeCategory)?.label || 'Alerte'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {filteredDocAlerts.map((alert) => (
                <AlertCard key={alert.id} alert={alert} onViewPendingDocument={onViewPendingDocument} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {showDocs && filteredDocAlerts.length === 0 && activeCategory !== 'toate' && (
        <Card>
          <CardContent className="py-8 text-center text-slate-500">
            <Bell className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">Nicio alertă în această categorie</p>
          </CardContent>
        </Card>
      )}

      {/* Maintenance alerts section */}
      {showMaintenance && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wrench className="w-5 h-5 text-blue-600" />
              Alerte Mentenanță
              {maintenanceAlerts.length > 0 && (
                <span className="text-xs font-normal text-slate-500">({maintenanceAlerts.length})</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingMaint ? (
              <p className="text-slate-500 text-sm">Se încarcă...</p>
            ) : maintenanceAlerts.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Wrench className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Nicio alertă de mentenanță</p>
              </div>
            ) : (
              <div className="space-y-2">
                {maintenanceAlerts.map(alert => (
                  <MaintenanceAlertCard key={alert.id} alert={alert} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Fuel alerts section */}
      {showFuel && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Fuel className="w-5 h-5 text-orange-600" />
              Alerte Alimentări Suspecte
              {fuelAlerts.length > 0 && (
                <span className="text-xs font-normal text-slate-500">({fuelAlerts.length})</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingFuel ? (
              <p className="text-slate-500 text-sm">Se încarcă...</p>
            ) : fuelAlerts.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Fuel className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Nicio alimentare suspectă</p>
              </div>
            ) : (
              <div className="space-y-2">
                {fuelAlerts.map((alert) => (
                  <div key={alert.id} className="p-3 border border-orange-200 bg-orange-50 rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4 text-orange-600" />
                          <span className="font-semibold text-orange-900">
                            {alert.driver_name} / {alert.car_license}
                          </span>
                          <span className="text-xs text-orange-700">{alert.date}</span>
                        </div>
                        <div className="text-sm text-orange-800">
                          <span className="font-bold">{alert.liters}L</span>
                          {alert.cost && <span className="ml-2">{alert.cost} RON</span>}
                          {alert.km_at_fuel && <span className="ml-2">@ {alert.km_at_fuel} km</span>}
                        </div>
                        <div className="mt-1 space-y-0.5">
                          {alert.reasons.map((reason, idx) => (
                            <p key={idx} className="text-xs text-orange-700">
                              {reason.message}
                              {reason.excess_percent > 0 && (
                                <span className="font-bold ml-1">(+{reason.excess_percent}%)</span>
                              )}
                            </p>
                          ))}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleVerifyFuelAlert(alert.id)}
                        disabled={verifyingId === alert.id}
                        className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                      >
                        {verifyingId === alert.id ? '...' : 'OK'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Supplier due-date alerts section */}
      {showSupplierDue && (
        <Card data-testid="supplier-due-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="w-5 h-5 text-amber-600" />
              Scadențe furnizori (următoarele 7 zile + restante)
              {supplierDue.alerts.length > 0 && (
                <span className="text-xs font-normal text-slate-500" data-testid="supplier-due-summary">
                  ({supplierDue.alerts.length} facturi · {fmtLei(supplierDue.total_neachitat)} rest de plată)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {supplierDue.alerts.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Receipt className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Nicio factură neachitată cu scadență apropiată</p>
              </div>
            ) : (
              <div className="space-y-2">
                {supplierDue.alerts.map(alert => <SupplierDueCard key={alert.id} alert={alert} />)}
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-3">Soldul din Saga reflectă ultimul import; plățile înregistrate manual în Documente furnizori scad restul de plată.</p>
          </CardContent>
        </Card>
      )}

      {/* Empty state for "Toate" when nothing exists */}
      {activeCategory === 'toate' && sortedAlerts.length === 0 && maintenanceAlerts.length === 0 && equipmentAlerts.length === 0 && fuelAlerts.length === 0 && supplierDue.alerts.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-base font-medium">Nicio alertă</p>
            <p className="text-sm mt-1">Toate documentele sunt valide</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default AlerteTab;
